"use client";

import { useInterfere } from "@interfere/next/provider";
import { useRouter } from "next/navigation";
import { createContext, use, useEffect, useState } from "react";
import type { SessionUser } from "@/lib/auth";
import { createClient } from "@/lib/supabase/client";

interface AuthState {
  isLoading: boolean;
  signOut: () => Promise<void>;
  user: SessionUser | null;
}

const AuthContext = createContext<AuthState>({
  isLoading: true,
  signOut: () => Promise.resolve(),
  user: null,
});

const IDENTITY_SOURCE = { name: "Supabase", type: "custom" } as const;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();
  const { identity } = useInterfere();

  useEffect(() => {
    const supabase = createClient();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const sessionUser = session?.user;
      setUser(
        sessionUser
          ? { email: sessionUser.email ?? null, id: sessionUser.id }
          : null
      );
      setIsLoading(false);

      if (sessionUser) {
        identity.set({
          email: sessionUser.email ?? undefined,
          identifier: sessionUser.id,
          source: IDENTITY_SOURCE,
        });
      } else {
        identity.clear();
      }
    });

    return () => subscription.unsubscribe();
  }, [identity]);

  const signOut = async () => {
    await createClient().auth.signOut();
    router.push("/");
    router.refresh();
  };

  return (
    <AuthContext.Provider value={{ isLoading, signOut, user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return use(AuthContext);
}
